import angular from 'angular'
import uuid from '@vend/vend-uuid'
import { VdToastNotificationService } from '@vend/vend.ui'

import {
  $interval,
  $q,
  $rootScope,
  $timeout,
  $window,
} from '../ng-compat/imports'

/**
 * Responsible for preventing multiple windows.
 *
 * @module duplicateWindowManager
 */
function duplicateWindowManager(
  vdToastNotificationService: VdToastNotificationService
) {
  const SESSION_KEY = 'VEND_WR_BROWSER_SESSION'
  const TIMESTAMP_KEY = 'VEND_WR_TIMESTAMP'
  const DUPLICATE_MSG =
    'Do you have another sell screen open? You should either close or refresh this window.'

  const updateSessionInterval = 10000
  let _sessionId: string | null = uuid()
  let _duplicateListener: $TSFixMe

  // ============================== Private API ============================== //

  /**
   * Set the current time within localstorage.
   *
   * @private
   * @method _setSessionTimeInStorage
   *
   */
  function _setSessionTimeInStorage() {
    $window.localStorage.setItem(TIMESTAMP_KEY, Date.now() as $TSFixMe)
  }

  /**
   * Retrieve the session time within localstorage.
   *
   * @private
   * @method _getSessionTimeFromStorage
   *
   */
  function _getSessionTimeFromStorage() {
    const storedSessionTime = $window.localStorage.getItem(TIMESTAMP_KEY)

    if (storedSessionTime) {
      return parseInt(storedSessionTime, 10)
    }
  }

  /**
   * Set a unique browser session id within localstorage.
   *
   * @private
   * @method _setSessionIdInStorage
   *
   */
  function _setSessionIdInStorage() {
    $window.localStorage.setItem(SESSION_KEY, _sessionId!)
  }

  /**
   * Retrieve the browser session id within localstorage.
   *
   * @private
   * @method _getSessionIdFromStorage
   *
   */
  function _getSessionIdFromStorage() {
    return $window.localStorage.getItem(SESSION_KEY)
  }

  /**
   * Sets the required items in localstorage.
   *
   * @private
   * @method _setLocalStorageItems
   *
   */
  function _setLocalStorageItems() {
    _setSessionIdInStorage()
    _setSessionTimeInStorage()
  }

  /**
   * Cancels the _duplicateListener interval
   *
   * @private
   * @method _stopDuplicateListener
   */
  function _stopDuplicateListener() {
    if (_duplicateListener) {
      $interval.cancel(_duplicateListener)
      _duplicateListener = undefined
    }
  }

  /**
   * Removes the session items from localStorage, clears the sessionId and
   * cancels the duplicate window listener
   *
   * @private
   * @method _clearSessionItems
   *
   */
  function _clearSessionItems() {
    _sessionId = null
    $window.localStorage.removeItem(SESSION_KEY)
    $window.localStorage.removeItem(TIMESTAMP_KEY)

    _stopDuplicateListener()
  }

  /**
   * Checks the session time in localstorage. If the time difference is greater than updateSessionInterval then
   * we either have a stale window or a window that is asleep - so assume we're the only window.
   *
   * If the time difference is less than updateSessionInterval then either we have another active window or the
   * page has been refreshed and clean up of the localstorage failed.
   *
   * @private
   * @method _confirmSecondWindow
   *
   * @return {Promise<confirmSecondWindowDeferred>} A promise evaluating the presense of another sell screen
   * window
   */
  function _confirmSecondWindow() {
    const confirmSecondWindowDeferred = $q.defer()
    const storedTime = _getSessionTimeFromStorage()
    const timeDifference = Date.now() - storedTime!
    const confirmSecondWindowBuffer = 2000
    let secondaryIntervalCheck

    // Time difference is greater than updateSessionInterval then we either have a stale window or a
    // window that is asleep - so assume we're the only window (maybe a false negative).
    if (timeDifference > updateSessionInterval) {
      confirmSecondWindowDeferred.resolve(false)
    } else {
      // Either we have another active window or the page has been refreshed and clean up of the
      // localstorage failed.
      secondaryIntervalCheck = updateSessionInterval - timeDifference

      // If secondary interval check is less than 2 seconds, add a two second buffer to ensure
      // that we don't recheck too soon
      if (secondaryIntervalCheck < 2000) {
        secondaryIntervalCheck += confirmSecondWindowBuffer
      }

      $timeout(function () {
        const latestStoredTime = _getSessionTimeFromStorage()

        // If the latest stored time is greater than our previously retrieved time, we know there is
        // another window setting values in localStorage
        if (latestStoredTime! >= storedTime!) {
          confirmSecondWindowDeferred.resolve(true)
        } else {
          // Either a previous session was not cleared up correctly, or the other window is asleep. So we
          // assume we are the only window (maybe a false negative)
          confirmSecondWindowDeferred.resolve(false)
        }
      }, secondaryIntervalCheck)
    }
    return confirmSecondWindowDeferred.promise
  }

  /**
   * Initiated when we know the current window is the only window. Check's the browser sessionId in
   * localStorage with that stored in memory. Sets a new timestamp on every updateSessionInterval.
   *
   * @private
   * @method _listenForOtherWindow
   */
  function _listenForOtherWindow() {
    // Clear the required items when page is hidden; using pagehide since beforeunload never gets fired on iOS
    $window.addEventListener('pagehide', _clearSessionItems)

    // Remove the session items when we destroy rootScope and remove the pagehide event
    $rootScope.$on('$destroy', function () {
      _clearSessionItems()
      $window.removeEventListener('pagehide', _clearSessionItems)
    })

    _setLocalStorageItems()

    _duplicateListener = $interval(function () {
      const currentSessionId = _getSessionIdFromStorage()

      // Handle that rare instance where another window has removed our
      // session details, so prompt the user
      if (currentSessionId !== _sessionId) {
        vdToastNotificationService.negative({
          message: DUPLICATE_MSG,
        })
        _stopDuplicateListener()
      } else {
        _setSessionTimeInStorage()
      }
    }, updateSessionInterval)
  }

  // ============================== Public API ============================== //

  return {
    /**
     * Checks if there is a sessionId within localStorage. If there is not a sessionId, set the required
     * sessionItems and listen for the presence of another window. Otherwise if there is a sessionId, we
     * confirm either the existence of another window, or a failed cleanup.
     *
     * @method ensureSingleWindow
     * @return {Promise} A promise evaluating the presence of another sell screen
     */
    ensureSingleWindow() {
      const ensureSingleWindowDeferred = $q.defer()
      const currentSessionId = _getSessionIdFromStorage()

      if (currentSessionId) {
        _confirmSecondWindow().then(function (isAnotherWindow) {
          // Do we have a second window?
          if (isAnotherWindow) {
            ensureSingleWindowDeferred.reject()
          } else {
            // Either a stale window or a window that is asleep - so assume we're the only window
            // (maybe a false negative).
            _listenForOtherWindow()
            ensureSingleWindowDeferred.resolve()
          }
        })
      } else {
        // No session within localStorage, so resolve and listen for another window
        _listenForOtherWindow()
        ensureSingleWindowDeferred.resolve()
      }
      return ensureSingleWindowDeferred.promise
    },
  }
}

export type DuplicateWindowManager = ReturnType<typeof duplicateWindowManager>

angular
  .module('webregister.managers')
  .factory('duplicateWindowManager', duplicateWindowManager)
